import React, { useLayoutEffect, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './CragTechProject.css';

gsap.registerPlugin(ScrollTrigger);

const CT = '/content/cragtech/';
const CDN = 'https://pub-8a575460bea442e2a9d816d772948be2.r2.dev/';

interface CragTechProjectProps {
  theme?: string;
}

const CragTechProject: React.FC<CragTechProjectProps> = ({ theme = 'light' }) => {
  const rootRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useLayoutEffect(() => {
    document.body.className = 'light';
    return () => { document.body.className = theme; };
  }, [theme]);

  useEffect(() => {
    const ctx = gsap.context(() => {

      // Mount: hero title + subtitle (above the fold, don't wait for scroll)
      gsap.from('.ct-hero-in', {
        y: 24, opacity: 0, duration: 0.9, ease: 'power3.out', stagger: 0.1, delay: 0.2
      });

      // Initial states for scroll elements
      gsap.set('.ct-fade-up',    { y: 30,  opacity: 0 });
      gsap.set('.ct-fade-left',  { x: -52, opacity: 0 });
      gsap.set('.ct-fade-right', { x: 52,  opacity: 0 });
      gsap.set('.ct-fade-scale', { scale: 0.93, opacity: 0 });

      ScrollTrigger.batch('.ct-fade-up', {
        onEnter: (els) => gsap.to(els, { y: 0, opacity: 1, stagger: 0.09, duration: 0.85, ease: 'power3.out' }),
        start: 'top 90%', once: true,
      });
      ScrollTrigger.batch('.ct-fade-left', {
        onEnter: (els) => gsap.to(els, { x: 0, opacity: 1, stagger: 0.08, duration: 0.95, ease: 'power3.out' }),
        start: 'top 88%', once: true,
      });
      ScrollTrigger.batch('.ct-fade-right', {
        onEnter: (els) => gsap.to(els, { x: 0, opacity: 1, stagger: 0.08, duration: 0.95, ease: 'power3.out' }),
        start: 'top 88%', once: true,
      });
      ScrollTrigger.batch('.ct-fade-scale', {
        onEnter: (els) => gsap.to(els, { scale: 1, opacity: 1, stagger: 0.08, duration: 1, ease: 'power3.out' }),
        start: 'top 88%', once: true,
      });

    }, rootRef);
    return () => ctx.revert();
  }, []);

  const outcomes = [
    { title: 'Full visual identity from scratch —', desc: 'logo system, Prosty typeface, color system, and brand guidelines.' },
    { title: '3D phone model in Autodesk Maya —', desc: 'built from concept sketches, photorealistic renders used across every deliverable.' },
    { title: 'Two broadcast commercial formats —', desc: 'horizontal and vertical, composited in After Effects with particle systems and camera tracking.' },
    { title: 'Complete campaign asset library —', desc: 'billboards, LED displays, social posts, web banners, and branded merchandise.' },
  ];

  const users = [
    {
      num: '01', initial: 'M',
      name: 'Marcus', meta: '34 · Construction Site Engineer · Milwaukee, WI',
      context: 'Drops his phone twice a week on the job. Has replaced it three times in two years. Needs a device that survives a concrete floor without a second thought.',
      pains: ['Cracked screens from daily drops', 'Phones die mid-shift in the cold', 'Touchscreens fail with work gloves on'],
    },
    {
      num: '02', initial: 'J',
      name: 'Jordan', meta: '28 · Outdoor Enthusiast · Denver, CO',
      context: 'Summits 14ers most weekends. His phone dies by noon on long trails. GPS unreliability has forced him to turn back twice.',
      pains: ['Battery dead before the summit', 'No GPS signal in remote terrain', 'Water damage on wet trails'],
    },
    {
      num: '03', initial: 'P',
      name: 'Priya', meta: '31 · Field Research Scientist · Seattle, WA',
      context: 'Works in rain, mud, and saltwater environments. Needs a device that keeps running when conditions get ugly and connectivity is spotty.',
      pains: ['Water and dust ingress failure', 'Charging ports corrode in the field', 'Screens unresponsive when wet'],
    },
  ];

  const moodboard = [
    { file: '1.png', source: 'JerryRigEverything' },
    { file: '2.png', source: 'JerryRigEverything' },
    { file: '3.png', source: 'JerryRigEverything' },
    { file: '4.png', source: 'JerryRigEverything' },
    { file: '5.png', source: 'JerryRigEverything' },
    { file: '6.png', source: 'JerryRigEverything' },
    { file: '7.png', source: 'JerryRigEverything' },
    { file: '8.png', source: 'JerryRigEverything' },
    { file: 'Durable Phone Cases.png', source: 'dbrand' },
    { file: 'UAG iPhone 17 Air Case Features.png', source: 'UAG' },
    { file: 'UAG iPhone 17 Pro Monarch Pro.png', source: 'UAG' },
    { file: 'iPhone 13 MagSafe Grip.png', source: 'dbrand' },
    { file: 'Energizer Phone Comparison.png', source: 'The Verge' },
  ];

  const renders = [
    { file: 'back.png', label: 'Back' },
    { file: 'back side.png', label: 'Back side' },
    { file: 'screen.png', label: 'Screen' },
    { file: 'logo.png', label: 'Logo detail' },
  ];

  const oohMain = [
    { file: 'mockups/billboard.png', label: 'Billboard' },
    { file: 'mockups/LEDBillboardMockup1.png', label: 'LED Billboard' },
    { file: 'mockups/LEDBillboardMockup.png', label: 'Urban Installation' },
  ];

  const oohSocial = [
    { src: `${CT}mockups/socialmediapost.png`, label: 'Social Post' },
    { src: `${CT}mockups/socialmediastory.png`, label: 'Social Story' },
    { src: `${CT}mockups/${encodeURIComponent('Posts - Scaled Raster Image.png')}`, label: 'Digital Ad' },
    { src: `${CDN}cragtech-bag-mockup.png`, label: 'Branded Merch' },
  ];

  const references = [
    'openpr.com', 'tracki.com', 'accio.com', 'futuremarketresearch.com',
    'shedreamsofalpine.com', 'cognitivemarketresearch.com', 'doogee.com', 'cleverhiker.com',
    'datainsightsmarket.com', 'archivemarketresearch.com', 'hardwarezone.com.sg', 'conquest-ex.com',
    'youtube.com', 'eweek.com', 'marketreportanalytics.com', 'routzz.com',
    'linkedin.com', 'weareconker.com', 'energyelectronicsllc.com', 'trainwithdaveoc.com',
    'kyoceramobile.com', 'thecatphones.com', 'forinsightsconsultancy.com',
    'androidpolice.com', 'menshealth.com',
  ];

  return (
    <div className="ct" ref={rootRef}>

      {/* S1: Hero — full-screen video */}
      <section className="ct__hero-full">
        <video className="ct__hero-video" autoPlay loop muted playsInline>
          <source src={`${CDN}topo.mp4`} type="video/mp4" />
        </video>
        <div className="ct__hero-overlay" />
      </section>

      <div className="ct__container">

        {/* S2: Project Header */}
        <header className="ct__header">
          <h2 className="ct__title ct-hero-in">CragTech Matterhorn</h2>
          <p className="ct__descriptor ct-hero-in">
            Brand identity, product visualization, and advertising campaign for a rugged smartphone newcomer.
          </p>
          <div className="ct__meta">
            {[
              { label: 'Type', value: 'Brand Identity + Campaign' },
              { label: 'Year', value: '2024' },
              { label: 'Role', value: 'Brand Designer' },
              { label: 'Tools', value: 'Maya · After Effects · Illustrator' },
            ].map(({ label, value }) => (
              <div className="ct__meta-item ct-fade-up" key={label}>
                <div className="ct__meta-label">{label}</div>
                <div className="ct__meta-value">{value}</div>
              </div>
            ))}
          </div>
        </header>

        {/* S3: TL;DR + Key Outcomes */}
        <section className="ct__section">
          <div className="ct__split">
            <div>
              <span className="ct__tldr-tag ct-fade-up">tl;dr</span>
              <p className="ct__body ct-fade-up">
                CragTech had no brand, no product, and no market presence. The brief: build all three from scratch. The result was a complete visual identity, a 3D phone model built in Maya from concept sketches, two broadcast commercials composited in After Effects, and a full campaign library spanning billboards, social content, and branded merchandise.
              </p>
            </div>
            <ul className="ct__outcomes">
              {outcomes.map(({ title, desc }) => (
                <li className="ct__outcome ct-fade-up" key={title}>
                  <div className="ct__outcome-dot" />
                  <div>
                    <span className="ct__outcome-strong">{title} </span>
                    <span className="ct__outcome-desc">{desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* S4: The Brief */}
        <section className="ct__section">
          <div className="ct__section-label ct-fade-up">the brief</div>
          <div className="ct__split">
            <p className="ct__statement ct-fade-up">
              Build a brand and launch campaign for a phone nobody has heard of, in a market Apple and Samsung own.
            </p>
            <div className="ct-fade-up">
              <p className="ct__body">
                CragTech entered the smartphone market with no existing brand guidelines, no manufactured product, and no recognition. The brief demanded a complete visual identity, a 3D model of the phone built from concept sketches, and advertising capable of earning attention in a crowded space.
              </p>
              <p className="ct__body" style={{ marginTop: '1rem' }}>
                The product angle was clear: a high-end, super-durable smartphone for people whose jobs and hobbies destroy ordinary phones. Three days of battery life. Waterproof. Drop resistant. Functional across extreme temperatures.
              </p>
              <div className="ct__pill-strip ct-fade-up">
                {['3-day battery', 'Waterproof', 'Drop resistant', 'Extreme temps'].map(f => (
                  <span className="ct__pill" key={f}>{f}</span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* S5: Research — The Market */}
        <section className="ct__section">
          <div className="ct__section-label ct-fade-up">the market</div>
          <div className="ct__split">
            <p className="ct__statement ct-fade-up">
              The rugged phone market is growing, but largely invisible to mainstream consumers.
            </p>
            <ul className="ct__findings">
              <li className="ct__finding ct-fade-up">61% of consumers cite durability as a key factor when buying tech, yet rugged phones hold less than 3% market share.</li>
              <li className="ct__finding ct-fade-up">95% of enterprise users who switch to rugged devices never return to consumer phones.</li>
              <li className="ct__finding ct-fade-up">Rugged devices can cut business mobility costs by 46% through extended lifespan and fewer replacements.</li>
              <li className="ct__finding ct-fade-up">The fastest-growing segment is not enterprise — it is outdoor enthusiasts, driven by hiking, camping, and adventure sports.</li>
              <li className="ct__finding ct-fade-up">Battery life and GPS reliability are the two non-negotiable features across all user groups.</li>
            </ul>
          </div>
        </section>

        {/* S6: Understanding Users */}
        <section className="ct__section">
          <div className="ct__section-label ct-fade-up">understanding users</div>
          <div className="ct__split">
            <p className="ct__statement ct-fade-up">
              Three distinct users, one shared problem: existing phones break when they matter most.
            </p>
            <p className="ct__body ct-fade-up">
              Market research pointed to three overlapping segments — each with different environments, but the same core failure. Their phone could not handle where they needed to go.
            </p>
          </div>
          <div className="ct__personas-grid">
            {users.map(({ num, initial, name, meta, context, pains }) => (
              <div className="ct__persona ct-fade-up" key={name}>
                <div className="ct__persona-num">{num}</div>
                <div className="ct__persona-avatar">{initial}</div>
                <div className="ct__persona-name">{name}</div>
                <div className="ct__persona-role">{meta}</div>
                <p className="ct__persona-detail">{context}</p>
                <ul className="ct__persona-pains">
                  {pains.map(p => <li key={p}>{p}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </section>

      </div>

      {/* Moodboard — full bleed */}
      <section className="ct__moodboard-section">
        <div className="ct__container">
          <div className="ct__section-label ct-fade-up">visual research</div>
          <p className="ct__statement ct-fade-up ct__moodboard-statement">
            References that shaped the visual language: raw, functional, uncompromising.
          </p>
          <div className="ct__moodboard-grid">
            {moodboard.map(({ file, source }, i) => (
              <div className="ct__moodboard-item ct-fade-scale" key={i}>
                <img
                  src={`${CT}moodboard/${encodeURIComponent(file)}`}
                  alt={`Reference ${i + 1}`}
                  className="ct__moodboard-img"
                />
                <span className="ct__moodboard-source">{source}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="ct__container">

        {/* Design Language */}
        <section className="ct__section">
          <div className="ct__section-label ct-fade-up">design language</div>
          <div className="ct__split">
            <p className="ct__statement ct-fade-up">
              A monochromatic system built for durability: bold, precise, and unafraid of black.
            </p>
            <p className="ct__body ct-fade-up">
              Two typefaces carry the brand. Prosty Wide handles all display moments — the big title, the phone name, every campaign lockup. Poppins carries body copy and UI text at lighter weights. The color system is intentionally stripped: black, white, and a controlled range of grays. No accent colors. No gradients. The aesthetic earns its authority through restraint.
            </p>
          </div>

          {/* Typography */}
          <div className="ct__ds-type-grid ct-fade-scale">
            <div className="ct__ds-group">
              <div className="ct__ds-group-label">Display typeface</div>
              <img src={`${CT}Prosty Wide.svg`} alt="Prosty Wide" className="ct__ds-type-img" />
            </div>
            <div className="ct__ds-group">
              <div className="ct__ds-group-label">Body typeface</div>
              <img src={`${CT}Poppins.svg`} alt="Poppins" className="ct__ds-type-img" />
            </div>
          </div>

          {/* Logo system */}
          <div className="ct__logos-grid ct-fade-scale">
            <img src={`${CT}logos.svg`} alt="Logo variations" className="ct__logos-img" />
            <img src={`${CT}logos2.svg`} alt="Logo applications" className="ct__logos-img" />
          </div>
        </section>

        {/* 3D Modeling */}
        <section className="ct__section">
          <div className="ct__section-label ct-fade-up">3d modeling</div>
          <div className="ct__split">
            <p className="ct__statement ct-fade-up">
              Every pixel in the campaign comes from a model built entirely from scratch in Autodesk Maya.
            </p>
            <div className="ct-fade-up">
              <p className="ct__body">
                Starting from concept sketches, I modeled the complete Matterhorn phone in Maya — geometry, textures, materials, and lighting — until the renders were indistinguishable from a product photograph. These became the master assets used across every deliverable: commercials, billboards, social posts, and web banners.
              </p>
              <img src={`${CT}Autodesk Maya Logo.svg`} alt="Autodesk Maya" className="ct__tool-logo ct-fade-up" />
            </div>
          </div>

          {/* Maya workflow video */}
          <div className="ct__media-wide ct-fade-scale">
            <video className="ct__wide-video" autoPlay loop muted playsInline>
              <source src={`${CDN}cragtech-maya-workflow.mp4`} type="video/mp4" />
            </video>
          </div>

          {/* 3D renders */}
          <div className="ct__renders-grid">
            {renders.map(({ file, label }) => (
              <div className="ct__render-item ct-fade-scale" key={label}>
                <img
                  src={`${CT}renders/${encodeURIComponent(file)}`}
                  alt={label}
                  className="ct__render-img"
                />
                <span className="ct__render-label">{label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Storyboarding */}
        <section className="ct__section">
          <div className="ct__section-label ct-fade-up">storyboarding</div>
          <div className="ct__split">
            <p className="ct__statement ct-fade-up">
              The conceptualization phase lived mostly inside my head.
            </p>
            <p className="ct__body ct-fade-up">
              Shot composition, narrative pacing, and camera movement were worked out mentally before committing to a single frame. The storyboard below captured the bones of the sequence — enough to guide production without over-constraining it.
            </p>
          </div>
          <div className="ct__artifact-wrap ct-fade-scale">
            <img src={`${CT}storyboarding.png`} alt="Storyboard" className="ct__artifact-img" />
          </div>
        </section>

        {/* Commercial Production */}
        <section className="ct__section">
          <div className="ct__section-label ct-fade-up">commercial production</div>
          <div className="ct__split">
            <p className="ct__statement ct-fade-up">
              Maya renders exported to After Effects. Post-production brought the campaign to broadcast quality.
            </p>
            <div className="ct-fade-up">
              <p className="ct__body">
                High-resolution render passes from Maya were composited in After Effects using fractal noise for environmental texture, camera tracking for 3D depth, and particle systems to simulate dust, water, and impact effects. Color grading and broadcast output were finalized in DaVinci Resolve Studio.
              </p>
              <div className="ct__software-row ct-fade-up">
                <img src={`${CT}Adobe After Effects CC Icon.svg`} alt="After Effects" className="ct__software-icon" />
                <img src={`${CT}Adobe Illustrator CC Icon.svg`} alt="Illustrator" className="ct__software-icon" />
                <img src={`${CT}Adobe Photoshop CC Icon.svg`} alt="Photoshop" className="ct__software-icon" />
                <img src={`${CT}DaVinci Resolve Studio.png`} alt="DaVinci Resolve" className="ct__software-icon" />
              </div>
            </div>
          </div>
        </section>

      </div>

      {/* Final commercial — full bleed dark section */}
      <section className="ct__commercial-section">
        <div className="ct__container">
          <div className="ct__section-label ct-fade-up" style={{ color: 'rgba(255,255,255,0.35)' }}>the final commercial</div>
          <p className="ct__statement ct-fade-up" style={{ color: '#ffffff', maxWidth: '520px', marginBottom: '2.5rem' }}>
            Shot in two formats. Horizontal for streaming and YouTube. Vertical for social.
          </p>
        </div>
        <div className="ct__commercial-wrap ct-fade-scale">
          <video className="ct__commercial-video" autoPlay loop muted playsInline controls>
            <source src={`${CDN}cragtech-final.mp4`} type="video/mp4" />
          </video>
        </div>
      </section>

      <div className="ct__container">

        {/* Out of Home */}
        <section className="ct__section">
          <div className="ct__section-label ct-fade-up">out of home</div>
          <div className="ct__split">
            <p className="ct__statement ct-fade-up">
              Campaign assets for every surface, from interstate billboards to Instagram stories.
            </p>
            <p className="ct__body ct-fade-up">
              The same 3D renders that powered the commercials were adapted into a full asset library. Billboards, LED displays, web banners, social posts, stories, and branded merchandise — all carrying the same tight visual language.
            </p>
          </div>

          <div className="ct__ooh-showcase">
            <div className="ct__ooh-wide ct-fade-scale">
              <img src={`${CT}mockups/webbanner.png`} alt="Web banner" className="ct__ooh-img" />
            </div>

            <div className="ct__ooh-grid">
              {oohMain.slice(0, 2).map(({ file, label }) => (
                <div className="ct__ooh-item ct-fade-scale" key={label}>
                  <img src={`${CT}${file}`} alt={label} className="ct__ooh-img" />
                </div>
              ))}
            </div>

            <div className="ct__ooh-center">
              <div className="ct__ooh-item ct-fade-scale">
                <img src={`${CT}${oohMain[2].file}`} alt={oohMain[2].label} className="ct__ooh-img" />
              </div>
            </div>
          </div>

          <div className="ct__ooh-social-grid">
            {oohSocial.map(({ src, label }) => (
              <div className="ct__ooh-social-item ct-fade-scale" key={label}>
                <img src={src} alt={label} className="ct__ooh-img" />
              </div>
            ))}
          </div>
        </section>

        {/* References */}
        <section className="ct__section">
          <div className="ct__section-label ct-fade-up">references</div>
          <div className="ct__split">
            <p className="ct__statement ct-fade-up">Research and market data sourced from industry publications.</p>
            <div className="ct__references ct-fade-up">
              {references.map(ref => (
                <a
                  className="ct__reference-item"
                  key={ref}
                  href={`https://${ref}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >{ref}</a>
              ))}
            </div>
          </div>
        </section>

        {/* Closing */}
        <div className="ct__closing">
          <p className="ct__closing-copy ct-fade-up">
            Indiana University · Brand Design Curriculum · 2024
          </p>
        </div>

      </div>
    </div>
  );
};

export default CragTechProject;
